import type { BomItem } from "../types";

export type CatalogView = "all" | "parts" | "boms";
export type CatalogLayout = "grid" | "list";

export const CATALOG_LAYOUT_KEY = "sc_catalog_layout_v1";

export type CatalogJobInput = {
  drawing_id: string;
  job_id?: string;
  file_path?: string;
  name?: string;
  status?: string;
  /**
   * Line count, or the line rows themselves.
   * `GET /v1/jobs` does not read `bom_items`. The engine then
   * `setdefault("items", 0)`, so a bare 0 is not a measured count.
   */
  items?: number | unknown[];
  /** Set when `items` was measured (detail payload, fixture, or a positive list count). */
  itemCountKnown?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  has_thumbnail?: boolean;
  thumbnail_url?: string | null;
};

export type CatalogBomCard = {
  kind: "bom";
  id: string;
  jobId: string;
  drawingId: string;
  name: string;
  status?: string;
  /** Null until a real line count exists. A list-endpoint 0 is left null, not shown as "0 items". */
  itemCount: number | null;
  thumbnailUrl: string | null;
  updatedAt: string | null;
};

export type CatalogPartLine = {
  item: BomItem;
  parentJobId: string;
  parentName: string;
  parentUpdatedAt: string | null;
};

export type CatalogPartCard = {
  kind: "part";
  id: string;
  partNo: string;
  description: string;
  material: string;
  thumbnailUrl: string | null;
  costTry: number | null;
  qtyOnHand: number | null;
  parentJobId: string;
  parentName: string;
  parentUpdatedAt: string | null;
  bomItem: BomItem;
  usedInCount: number;
};

export type CatalogCard = CatalogBomCard | CatalogPartCard;

export function parseCatalogView(raw: string | null | undefined): CatalogView {
  if (raw === "parts" || raw === "boms" || raw === "all") return raw;
  return "all";
}

export function parseCatalogLayout(raw: string | null | undefined): CatalogLayout {
  return raw === "list" ? "list" : "grid";
}

export function loadCatalogLayout(): CatalogLayout {
  if (typeof window === "undefined") return "grid";
  try {
    return parseCatalogLayout(window.localStorage.getItem(CATALOG_LAYOUT_KEY));
  } catch {
    return "grid";
  }
}

export function saveCatalogLayout(layout: CatalogLayout): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CATALOG_LAYOUT_KEY, layout);
  } catch {
    /* ignore quota / private mode */
  }
}

export function jobKey(job: CatalogJobInput): string {
  return job.job_id || job.drawing_id;
}

export function jobDisplayName(job: CatalogJobInput): string {
  const raw = job.file_path || job.name || job.job_id || job.drawing_id || "";
  const leaf = raw.split(/[\\/]/).pop() || raw;
  return leaf || "—";
}

const PARSE_IN_FLIGHT = new Set([
  "pending",
  "queued",
  "parsing",
  "running",
  "uploaded",
  "uploading",
]);

/** Jobs still in the parse pipeline have no baked PNG yet. */
export function isCatalogParseInFlight(status: string | null | undefined): boolean {
  return PARSE_IN_FLIGHT.has((status || "").toLowerCase());
}

/** Extension shown on a missing preview (STEP, DXF, …). Empty when the name has none. */
export function fileTypeMark(name: string | null | undefined): string {
  const leaf = (name || "").split(/[\\/]/).pop() || "";
  const dot = leaf.lastIndexOf(".");
  if (dot <= 0 || dot === leaf.length - 1) return "";
  const ext = leaf.slice(dot + 1).replace(/[^a-z0-9]/gi, "");
  if (!ext || ext.length > 4) return "";
  return ext.toUpperCase();
}

export function jobThumbnailUrl(job: CatalogJobInput): string | null {
  /** Bump when engine thumb bake preference/camera changes so the browser refetches. */
  const rev = "i4";
  const id = jobKey(job);
  let url: string | null = null;
  if (job.thumbnail_url) url = job.thumbnail_url;
  else if (job.has_thumbnail && id) {
    url = `/v1/jobs/${encodeURIComponent(id)}/artifacts/thumbnail`;
  } else if (id && !isCatalogParseInFlight(job.status)) {
    // GET /v1/jobs/{id}/artifacts/thumbnail bakes on miss (ensure_job_thumbnail)
    // and falls back to the MinIO artifact. One attempt; 404s are cached.
    url = `/v1/jobs/${encodeURIComponent(id)}/artifacts/thumbnail`;
  }
  if (!url) return null;
  if (
    url.startsWith("/demo/") ||
    url.startsWith("blob:") ||
    url.startsWith("data:")
  ) {
    return url;
  }
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}v=${rev}`;
}

/**
 * Line count for a BOM card.
 *
 * The detail page counts `GET /v1/jobs/{id}` `items.length`. The list endpoint
 * does not load those lines and the engine defaults `items` to 0, so a bare 0
 * must not be shown. An items array (detail payload or the demo fixture) and
 * a positive number are real counts. A confirmed empty BOM is `items: 0` with
 * `itemCountKnown`.
 *
 * Backend: have `GET /v1/jobs` return `COUNT(*)` from `bom_items` (or the
 * stored response array length) instead of `setdefault("items", 0)` in
 * `_enrich_job_list_item`. Until then the catalog fills the count from getJob.
 */
export function bomItemCount(
  job: Pick<CatalogJobInput, "items" | "itemCountKnown">,
): number | null {
  if (Array.isArray(job.items)) return job.items.length;
  if (typeof job.items !== "number" || !Number.isFinite(job.items) || job.items < 0) {
    return null;
  }
  if (job.items === 0 && !job.itemCountKnown) return null;
  return job.items;
}

/**
 * Count worth keeping from a list row. Drops the engine's default 0 so it
 * cannot overwrite a count that already came from the detail payload.
 */
export function trustedListItemCount(items: unknown): number | undefined {
  if (Array.isArray(items)) return items.length;
  if (typeof items === "number" && Number.isFinite(items) && items > 0) return items;
  return undefined;
}

/** Overlay a count measured from the same `items` array the detail page uses. */
export function withHydratedItemCount(
  card: CatalogBomCard,
  count: number | undefined,
): CatalogBomCard {
  if (count == null) return card;
  return { ...card, itemCount: count };
}

export function jobToBomCard(job: CatalogJobInput): CatalogBomCard {
  const id = jobKey(job);
  return {
    kind: "bom",
    id,
    jobId: id,
    drawingId: job.drawing_id || id,
    name: jobDisplayName(job),
    status: job.status,
    itemCount: bomItemCount(job),
    thumbnailUrl: jobThumbnailUrl(job),
    updatedAt: job.updated_at || job.created_at || null,
  };
}

export function mapJobItemsToBom(raw: unknown, _jobId?: string): BomItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((r, i) => {
    const row = (r && typeof r === "object" ? r : {}) as Record<string, unknown>;
    const matched = row.matched_stock as BomItem["matched_stock"];
    const itemNo = Number(row.item_no ?? i + 1);
    // Only the URL the engine stored after a successful part bake. Inventing
    // thumbnail_part_{n} asks for a PNG that was never written and 404s.
    const thumb =
      typeof row.thumbnail_url === "string" && row.thumbnail_url
        ? String(row.thumbnail_url)
        : null;
    const rawRow =
      row.raw_row_data && typeof row.raw_row_data === "object"
        ? (row.raw_row_data as Record<string, unknown>)
        : row;
    const num = (v: unknown): number | undefined =>
      typeof v === "number" && Number.isFinite(v) ? v : undefined;
    return {
      item_no: itemNo,
      part_no: String(row.part_no ?? ""),
      qty: Number(row.qty ?? row.quantity ?? 1),
      description: String(row.description ?? ""),
      material: String(row.material ?? ""),
      unit: String(row.unit ?? "adet"),
      confidence: Number(row.confidence ?? 0),
      risk_level: row.risk_level != null ? String(row.risk_level) : undefined,
      // Decision and costing fields the engine sets — the BOM tree and part
      // detail read these, and dropping them silently reads as "undecided".
      suggested_action:
        row.suggested_action != null ? String(row.suggested_action) : undefined,
      shortage_qty: num(row.shortage_qty),
      est_material_cost_try: num(row.est_material_cost_try),
      matched_stock: matched ?? null,
      thumbnail_url: thumb,
      raw_row_data: rawRow,
    };
  });
}

function partKey(partNo: string): string {
  return partNo.trim().toLowerCase();
}

function stamp(iso: string | null | undefined): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : 0;
}

export function aggregateParts(lines: CatalogPartLine[]): CatalogPartCard[] {
  const byKey = new Map<string, CatalogPartCard>();
  for (const line of lines) {
    const key = partKey(line.item.part_no);
    if (!key) continue;
    const stock = line.item.matched_stock;
    const next: CatalogPartCard = {
      kind: "part",
      id: key,
      partNo: line.item.part_no,
      description: line.item.description,
      material: line.item.material,
      thumbnailUrl: line.item.thumbnail_url ?? null,
      costTry:
        typeof stock?.unit_price_try === "number" ? stock.unit_price_try : null,
      qtyOnHand: typeof stock?.stock_qty === "number" ? stock.stock_qty : null,
      parentJobId: line.parentJobId,
      parentName: line.parentName,
      parentUpdatedAt: line.parentUpdatedAt,
      bomItem: line.item,
      usedInCount: 1,
    };
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, next);
      continue;
    }
    const keepNewer =
      stamp(line.parentUpdatedAt) >= stamp(prev.parentUpdatedAt);
    const kept = keepNewer ? next : prev;
    kept.usedInCount = prev.usedInCount + 1;
    if (keepNewer && prev.costTry != null && next.costTry == null) {
      kept.costTry = prev.costTry;
    }
    if (keepNewer && prev.qtyOnHand != null && next.qtyOnHand == null) {
      kept.qtyOnHand = prev.qtyOnHand;
    }
    if (keepNewer && !next.thumbnailUrl && prev.thumbnailUrl) {
      kept.thumbnailUrl = prev.thumbnailUrl;
    }
    byKey.set(key, kept);
  }
  return [...byKey.values()];
}

function cardHaystack(card: CatalogCard): string {
  if (card.kind === "bom") return card.name;
  return [card.partNo, card.description, card.material].join(" ");
}

export function filterCatalog(
  cards: CatalogCard[],
  view: CatalogView,
  query: string,
): CatalogCard[] {
  const q = query.trim().toLowerCase();
  return cards.filter((card) => {
    if (view === "parts" && card.kind !== "part") return false;
    if (view === "boms" && card.kind !== "bom") return false;
    if (!q) return true;
    return cardHaystack(card).toLowerCase().includes(q);
  });
}

export function isReviewStatus(status: string | undefined): boolean {
  const s = (status || "").toUpperCase();
  return s === "HITL" || s === "PARTIAL";
}

export function mergeJobLists(
  server: CatalogJobInput[],
  local: CatalogJobInput[],
): CatalogJobInput[] {
  const byKey = new Map<string, CatalogJobInput>();
  for (const r of local) {
    const k = jobKey(r);
    if (k) byKey.set(k, r);
  }
  for (const r of server) {
    const k = jobKey(r);
    if (k) byKey.set(k, { ...byKey.get(k), ...r });
  }
  return [...byKey.values()].sort((a, b) => {
    const ta = Date.parse(a.updated_at || a.created_at || "") || 0;
    const tb = Date.parse(b.updated_at || b.created_at || "") || 0;
    return tb - ta;
  });
}

export function sortCatalog(cards: CatalogCard[]): CatalogCard[] {
  return [...cards].sort((a, b) => {
    const ta = stamp(a.kind === "bom" ? a.updatedAt : a.parentUpdatedAt);
    const tb = stamp(b.kind === "bom" ? b.updatedAt : b.parentUpdatedAt);
    return tb - ta;
  });
}
